import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

const STATUS_COLORS = {
  active:    { bg: "var(--success-soft)", color: "var(--success)" },
  retired:   { bg: "var(--bg-2)",         color: "var(--muted)"   },
  pending:   { bg: "var(--warn-soft)",    color: "var(--warn)"    },
  scheduled: { bg: "var(--primary-soft)", color: "var(--primary)" },
  executing: { bg: "var(--warn-soft)",    color: "var(--warn)"    },
  executed:  { bg: "var(--success-soft)", color: "var(--success)" },
  confirmed: { bg: "var(--success-soft)", color: "var(--success)" },
  failed:    { bg: "var(--danger-soft)",  color: "var(--danger)"  },
  cancelled: { bg: "var(--bg-2)",         color: "var(--muted)"   },
};

function Pill({ label, testId }) {
  const t = STATUS_COLORS[label] || { bg: "var(--bg-2)", color: "var(--muted)" };
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ background: t.bg, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {(label || "—").replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

const emptyRule = {
  name: "", description: "", data_category: "",
  linked_purposes: [], linked_data_categories: [], linked_assets: [],
  trigger_type: "purpose_completion", retention_period_days: 365, grace_period_days: 0,
  legal_basis: "", statutory_ref: "",
  deletion_method: "hard_delete",
  processor_notification_required: true, pre_erasure_notice_required: false,
  approval_required: false, evidence_required: true,
  responsible_role: "dpo", status: "active",
};

const emptyTask = {
  principal_id: "", activity_id: "", asset_id: "", rule_id: "",
  trigger: "manual", method: "hard_delete", scheduled_for: "", notes: "",
};

export default function RetentionRules() {
  const [tab, setTab] = useState("dashboard");
  const [meta, setMeta] = useState({ triggers: [], methods: [], task_statuses: [], statutory_library: [] });
  const [dash, setDash] = useState(null);
  const [rules, setRules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fTrigger, setFTrigger] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [fTaskStatus, setFTaskStatus] = useState("");
  const [fTaskTrigger, setFTaskTrigger] = useState("");
  const [fTaskSearch, setFTaskSearch] = useState("");

  const [selection, setSelection] = useState({});
  const [ruleModal, setRuleModal] = useState(false);
  const [ruleForm, setRuleForm] = useState(emptyRule);
  const [editingRule, setEditingRule] = useState(null);

  const [taskModal, setTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState(emptyTask);

  const [transitionModal, setTransitionModal] = useState(null); // task obj

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, d, r, t] = await Promise.all([
        api.get("/retention-meta/config"),
        api.get("/retention/dashboard"),
        api.get("/retention/rules", { params: { limit: 500, trigger_type: fTrigger, status: fStatus, search: fSearch } }),
        api.get("/retention/deletion-tasks", { params: { limit: 500, status: fTaskStatus, trigger: fTaskTrigger, search: fTaskSearch } }),
      ]);
      setMeta(m.data);
      setDash(d.data);
      setRules(r.data.items || []);
      setTasks(t.data.items || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { loadAll();   }, []);
  useEffect(() => { if (tab === "rules") loadAll();   }, [fTrigger, fStatus, fSearch]);
  useEffect(() => { if (tab === "queue") loadAll();   }, [fTaskStatus, fTaskTrigger, fTaskSearch]);

  const saveRule = async () => {
    try {
      if (editingRule) {
        await api.put(`/retention/rules/${editingRule}`, ruleForm);
        toast.success("Rule updated");
      } else {
        await api.post("/retention/rules", ruleForm);
        toast.success("Rule created");
      }
      setRuleModal(false); setEditingRule(null); setRuleForm(emptyRule);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEditRule = (r) => {
    setRuleForm({ ...emptyRule, ...r });
    setEditingRule(r.id);
    setRuleModal(true);
  };

  const removeRule = async (rid) => {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await api.delete(`/retention/rules/${rid}`);
      toast.success("Deleted"); await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const adoptStatutory = async (key) => {
    try {
      await api.post("/retention/rules/adopt-statutory", { key });
      toast.success("Adopted as a rule");
      await loadAll();
      setTab("rules");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const scanOverdue = async () => {
    toast.info("Scanning…");
    try {
      const { data } = await api.post("/retention/scan-overdue");
      toast.success(`${data.scheduled} deletion(s) scheduled`);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createTask = async () => {
    try {
      await api.post("/retention/deletion-tasks", taskForm);
      toast.success("Task created");
      setTaskModal(false); setTaskForm(emptyTask);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const transitionTask = async (payload) => {
    try {
      await api.post(`/retention/deletion-tasks/${transitionModal.id}/transition`, payload);
      toast.success(`Marked ${payload.status}`);
      setTransitionModal(null);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bulkAction = async (status) => {
    const ids = Object.keys(selection).filter(k => selection[k]);
    if (ids.length === 0) return toast.info("Select at least one task");
    if (!window.confirm(`Mark ${ids.length} task(s) as ${status}?`)) return;
    try {
      await api.post("/retention/deletion-tasks/bulk-transition", { task_ids: ids, status });
      toast.success("Updated");
      setSelection({});
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const selectedCount = useMemo(() => Object.values(selection).filter(Boolean).length, [selection]);

  if (loading && !dash) return <Loader />;

  const yearsFromDays = (d) => (d / 365).toFixed(1);

  return (
    <div>
      <PageHeader
        title="Retention & Data Minimization"
        kicker="DPDP Sec. 8(7) — Erasure & Retention"
        description="Statutory-aligned retention rules, automated deletion queue with proof-of-deletion, and gap detection."
        testId="retention-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={loadAll} data-testid="ret-refresh"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
            <Btn variant="secondary" onClick={scanOverdue} data-testid="ret-scan"><Icons.ScanLine className="w-3.5 h-3.5" /> Scan overdue</Btn>
            <button onClick={() => downloadFile("/retention/register.pdf", "retention_register.pdf")}
               className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11.5px] font-medium"
               style={{ background: "var(--bg-2)", color: "var(--ink)" }} data-testid="ret-pdf">
              <Icons.Download className="w-3.5 h-3.5" /> PDF register
            </button>
            <Btn onClick={() => { setRuleForm(emptyRule); setEditingRule(null); setRuleModal(true); }} data-testid="ret-new-rule">
              <Icons.Plus className="w-3.5 h-3.5" /> New rule
            </Btn>
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "rules", label: "Rules", icon: "ListChecks" },
          { key: "queue", label: "Deletion queue", icon: "Trash2" },
          { key: "statutory", label: "Statutory library", icon: "BookMarked" },
        ].map(t => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`ret-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{ background: tab === t.key ? "var(--primary-soft)" : "transparent", color: tab === t.key ? "var(--primary)" : "var(--muted)" }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dash && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Active rules" value={dash.active_rules} testId="stat-rules" hint={`of ${dash.total_rules} total`} />
            <StatCard label="Coverage" value={`${dash.coverage_pct}%`} tone={dash.coverage_pct < 80 ? "warn" : "success"} testId="stat-coverage"
                     hint={`${dash.assets_with_retention}/${dash.assets_total} assets`} />
            <StatCard label="Overdue assets" value={dash.overdue_assets} tone={dash.overdue_assets > 0 ? "danger" : "success"} testId="stat-overdue" />
            <StatCard label="Pending deletions" value={dash.pending_tasks} tone="warn" testId="stat-pending" />
            <StatCard label="Completed" value={dash.executed_tasks} tone="success" testId="stat-executed" />
            <StatCard label="Failed" value={dash.failed_tasks} tone="danger" testId="stat-failed" />
            <StatCard label="Purposes w/o rule" value={dash.purposes_without_rule?.length || 0} tone="warn" testId="stat-purpose-gap" />
            <StatCard label="Assets w/o retention" value={dash.assets_without_retention?.length || 0} tone="warn" testId="stat-asset-gap" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Rules by trigger</div>
              <div className="space-y-1.5" data-testid="by-trigger">
                {Object.entries(dash.by_trigger || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11.5px]">
                    <Icons.GitBranch className="w-3 h-3" style={{ color: "var(--primary)" }} />
                    <div className="flex-1 ink">{k.replace(/_/g, " ")}</div><div className="muted">{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Deletion queue by status</div>
              <div className="space-y-1.5" data-testid="by-status">
                {Object.entries(dash.by_status || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11.5px]">
                    <Pill label={k} />
                    <div className="flex-1" />
                    <div className="muted">{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Deletion trend</div>
              <div className="space-y-1.5" data-testid="trend">
                {(dash.trend || []).length === 0 ? (
                  <div className="text-[11.5px] muted">No completed deletions yet.</div>
                ) : (dash.trend || []).slice(-12).map(t => {
                  const max = Math.max(1, ...(dash.trend || []).map(x => x.count));
                  const pct = Math.round(t.count / max * 100);
                  return (
                    <div key={t.month}>
                      <div className="flex items-center gap-2 text-[11px]"><span className="flex-1 ink mono">{t.month}</span><span className="muted">{t.count}</span></div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                        <div className="h-full" style={{ width: `${pct}%`, background: "var(--success)" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3 flex items-center gap-2">
                <Icons.AlertTriangle className="w-3.5 h-3.5" style={{ color: "var(--warn)" }} />
                Assets missing a retention window
              </div>
              {(dash.assets_without_retention || []).length === 0 ? (
                <div className="text-[12px] muted">All assets have a retention window. 🎯</div>
              ) : (
                <div className="space-y-1.5" data-testid="gap-assets">
                  {dash.assets_without_retention.map((a, i) => (
                    <div key={a.id || `${a.name || ""}-${i}`} className="p-2 rounded-md flex items-center gap-2" style={{ background: "var(--bg-2)" }}>
                      <Icons.Database className="w-3.5 h-3.5" style={{ color: "var(--warn)" }} />
                      <div className="flex-1 text-[11.5px] ink truncate">{a.name || "—"}</div>
                      <div className="text-[10.5px] muted">{a.system}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Latest activity</div>
              <div className="space-y-1.5" data-testid="activity-feed">
                {(dash.activity_feed || []).length === 0 ? (
                  <div className="text-[12px] muted">No activity yet.</div>
                ) : (dash.activity_feed || []).map((ev, i) => (
                  <div key={ev.id || `${ev.at || ""}-${i}`} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                    <div className="text-[11.5px] ink"><b>{(ev.action || "").replace(/_/g, " ")}</b> · task {ev.task_id?.slice(0, 8)}</div>
                    <div className="text-[10px] muted">{ev.at?.slice(0, 19).replace("T", " ")} · {ev.actor}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "rules" && (
        <div>
          <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2">
            <TextInput data-testid="fr-search" placeholder="Search name / statute / category" value={fSearch} onChange={(e) => setFSearch(e.target.value)} />
            <SelectInput data-testid="fr-trigger" value={fTrigger} onChange={(e) => setFTrigger(e.target.value)}>
              <option value="">All triggers</option>
              {meta.triggers.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </SelectInput>
            <SelectInput data-testid="fr-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="active">active</option>
              <option value="retired">retired</option>
            </SelectInput>
          </div>
          {rules.length === 0 ? <EmptyState title="No rules yet" hint="Create one or adopt from the statutory library." /> : (
            <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <table className="w-full text-sm">
                <thead style={{ background: "var(--bg-2)" }}>
                  <tr>
                    {["Rule", "Trigger", "Days", "Method", "Statute", "Status", ""].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="rules-tbody">
                  {rules.map(r => (
                    <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`rule-row-${r.id}`}>
                      <td className="px-4 py-2 text-[12px] ink font-medium">{r.name}
                        {r.data_category && <div className="text-[10.5px] muted mt-0.5">{r.data_category}</div>}
                      </td>
                      <td className="px-4 py-2 text-[11.5px] muted">{(r.trigger_type || "").replace(/_/g, " ")}</td>
                      <td className="px-4 py-2 text-[11.5px] ink mono">{r.retention_period_days}
                        <span className="ml-1 muted">({yearsFromDays(r.retention_period_days || 0)}y)</span>
                      </td>
                      <td className="px-4 py-2 text-[11.5px] muted">{(r.deletion_method || "").replace(/_/g, " ")}</td>
                      <td className="px-4 py-2 text-[10.5px] muted">{r.statutory_ref || "—"}</td>
                      <td className="px-4 py-2"><Pill label={r.status} /></td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => openEditRule(r)} className="text-[11px] font-medium hover:underline mr-3" style={{ color: "var(--primary)" }} data-testid={`rule-edit-${r.id}`}>Edit</button>
                        <button onClick={() => removeRule(r.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }} data-testid={`rule-del-${r.id}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "queue" && (
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-2 justify-between">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1 max-w-3xl">
              <TextInput data-testid="ft-search" placeholder="Search principal / asset / notes" value={fTaskSearch} onChange={(e) => setFTaskSearch(e.target.value)} />
              <SelectInput data-testid="ft-status" value={fTaskStatus} onChange={(e) => setFTaskStatus(e.target.value)}>
                <option value="">All statuses</option>
                {meta.task_statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </SelectInput>
              <SelectInput data-testid="ft-trigger" value={fTaskTrigger} onChange={(e) => setFTaskTrigger(e.target.value)}>
                <option value="">All triggers</option>
                {meta.triggers.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </div>
            <div className="flex gap-2">
              <Btn size="sm" onClick={() => { setTaskForm(emptyTask); setTaskModal(true); }} data-testid="task-new"><Icons.Plus className="w-3 h-3" /> New task</Btn>
              {selectedCount > 0 && (
                <>
                  <Btn size="sm" variant="secondary" onClick={() => bulkAction("scheduled")} data-testid="bulk-schedule">Bulk schedule ({selectedCount})</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => bulkAction("executed")} data-testid="bulk-execute">Bulk execute</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => bulkAction("cancelled")} data-testid="bulk-cancel">Bulk cancel</Btn>
                </>
              )}
            </div>
          </div>
          {tasks.length === 0 ? <EmptyState title="No deletion tasks" hint="Trigger one manually, run the scanner, or wait for consent withdrawal cascades." /> : (
            <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <table className="w-full text-sm">
                <thead style={{ background: "var(--bg-2)" }}>
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox" data-testid="task-sel-all"
                        checked={tasks.length > 0 && tasks.every(t => selection[t.id])}
                        onChange={(e) => setSelection(e.target.checked ? Object.fromEntries(tasks.map(t => [t.id, true])) : {})} />
                    </th>
                    {["Trigger", "Principal / Asset", "Scheduled", "Method", "Status", "Proof", ""].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="tasks-tbody">
                  {tasks.map(t => (
                    <tr key={t.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`task-row-${t.id}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" data-testid={`task-sel-${t.id}`}
                          checked={!!selection[t.id]}
                          onChange={(e) => setSelection({ ...selection, [t.id]: e.target.checked })} />
                      </td>
                      <td className="px-4 py-2 text-[11.5px] muted">{(t.trigger || "").replace(/_/g, " ")}</td>
                      <td className="px-4 py-2 text-[11.5px] ink">
                        {t.principal_id && <div className="mono text-[10.5px]">P: {t.principal_id.slice(0, 12)}</div>}
                        {t.asset_id && <div className="mono text-[10.5px] muted">A: {t.asset_id.slice(0, 12)}</div>}
                        {!t.principal_id && !t.asset_id && <span className="muted">—</span>}
                      </td>
                      <td className="px-4 py-2 text-[10.5px] muted">{(t.scheduled_for || t.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-2 text-[11.5px] muted">{(t.method || "").replace(/_/g, " ")}</td>
                      <td className="px-4 py-2"><Pill label={t.status} testId={`task-status-${t.id}`} /></td>
                      <td className="px-4 py-2 text-[10.5px] mono muted">{t.proof_hash ? t.proof_hash.slice(0, 10) + "…" : "—"}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => setTransitionModal(t)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`task-open-${t.id}`}>Transition</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "statutory" && (
        <div>
          <div className="text-[12px] muted mb-3">Adopt a rule aligned with an Indian statute in one click. Values are pre-populated per each Act&apos;s requirements.</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="statutory-grid">
            {(meta.statutory_library || []).map(s => (
              <div key={s.key} className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
                <div className="flex items-start gap-2">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                    <Icons.BookOpen className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold ink">{s.act}</div>
                    <div className="text-[10.5px] muted mono">{s.section}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[13px] font-semibold" style={{ color: "var(--primary)" }}>{s.years}y</div>
                    <div className="text-[10px] muted">{s.days}d</div>
                  </div>
                </div>
                <div className="text-[11.5px] muted mt-2">{s.description}</div>
                <div className="mt-3">
                  <Btn size="sm" variant="secondary" onClick={() => adoptStatutory(s.key)} data-testid={`adopt-${s.key}`}>
                    <Icons.PlusCircle className="w-3 h-3" /> Adopt as rule
                  </Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RULE MODAL */}
      <Dialog open={ruleModal} onOpenChange={(v) => { if (!v) { setRuleModal(false); setEditingRule(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingRule ? "Edit rule" : "New retention rule"}</DialogTitle>
            <DialogDescription>Define what data must be retained or deleted, when, and how.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto">
            <Field label="Name" required className="col-span-2">
              <TextInput data-testid="r-name" value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} />
            </Field>
            <Field label="Description" className="col-span-2">
              <TextArea data-testid="r-desc" rows={2} value={ruleForm.description || ""} onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })} />
            </Field>
            <Field label="Data category">
              <TextInput data-testid="r-cat" value={ruleForm.data_category || ""} onChange={(e) => setRuleForm({ ...ruleForm, data_category: e.target.value })} />
            </Field>
            <Field label="Trigger">
              <SelectInput data-testid="r-trigger" value={ruleForm.trigger_type} onChange={(e) => setRuleForm({ ...ruleForm, trigger_type: e.target.value })}>
                {meta.triggers.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Retention (days)">
              <TextInput type="number" data-testid="r-days" value={ruleForm.retention_period_days} onChange={(e) => setRuleForm({ ...ruleForm, retention_period_days: Number(e.target.value) || 0 })} />
            </Field>
            <Field label="Grace period (days)">
              <TextInput type="number" data-testid="r-grace" value={ruleForm.grace_period_days || 0} onChange={(e) => setRuleForm({ ...ruleForm, grace_period_days: Number(e.target.value) || 0 })} />
            </Field>
            <Field label="Deletion method">
              <SelectInput data-testid="r-method" value={ruleForm.deletion_method} onChange={(e) => setRuleForm({ ...ruleForm, deletion_method: e.target.value })}>
                {meta.methods.map(m => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Statutory reference">
              <TextInput data-testid="r-stat" value={ruleForm.statutory_ref || ""} onChange={(e) => setRuleForm({ ...ruleForm, statutory_ref: e.target.value })} placeholder="e.g., Companies Act Sec. 128(5)" />
            </Field>
            <Field label="Responsible role">
              <SelectInput data-testid="r-role" value={ruleForm.responsible_role} onChange={(e) => setRuleForm({ ...ruleForm, responsible_role: e.target.value })}>
                {["dpo", "legal_reviewer", "security_officer", "business_owner", "org_admin"].map(r => <option key={r} value={r}>{r}</option>)}
              </SelectInput>
            </Field>
            <Field label="Legal basis" className="col-span-2">
              <TextArea data-testid="r-basis" rows={2} value={ruleForm.legal_basis || ""} onChange={(e) => setRuleForm({ ...ruleForm, legal_basis: e.target.value })} />
            </Field>
            <div className="col-span-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11.5px]">
              {[
                ["processor_notification_required", "Notify processor"],
                ["pre_erasure_notice_required", "Pre-erasure notice"],
                ["approval_required", "Approval required"],
                ["evidence_required", "Evidence required"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-1.5 p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                  <input type="checkbox" data-testid={`r-${k}`} checked={!!ruleForm[k]} onChange={(e) => setRuleForm({ ...ruleForm, [k]: e.target.checked })} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <Field label="Status">
              <SelectInput data-testid="r-status" value={ruleForm.status} onChange={(e) => setRuleForm({ ...ruleForm, status: e.target.value })}>
                <option value="active">active</option>
                <option value="retired">retired</option>
              </SelectInput>
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => { setRuleModal(false); setEditingRule(null); }}>Cancel</Btn>
            <Btn onClick={saveRule} disabled={!ruleForm.name} data-testid="r-save">{editingRule ? "Update" : "Create"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW TASK MODAL */}
      <Dialog open={taskModal} onOpenChange={setTaskModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create deletion task</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Field label="Principal ID"><TextInput data-testid="t-principal" value={taskForm.principal_id} onChange={(e) => setTaskForm({ ...taskForm, principal_id: e.target.value })} /></Field>
            <Field label="Asset ID"><TextInput data-testid="t-asset" value={taskForm.asset_id} onChange={(e) => setTaskForm({ ...taskForm, asset_id: e.target.value })} /></Field>
            <Field label="Activity ID"><TextInput data-testid="t-activity" value={taskForm.activity_id} onChange={(e) => setTaskForm({ ...taskForm, activity_id: e.target.value })} /></Field>
            <Field label="Trigger">
              <SelectInput data-testid="t-trigger" value={taskForm.trigger} onChange={(e) => setTaskForm({ ...taskForm, trigger: e.target.value })}>
                {meta.triggers.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Method">
              <SelectInput data-testid="t-method" value={taskForm.method} onChange={(e) => setTaskForm({ ...taskForm, method: e.target.value })}>
                {meta.methods.map(m => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Scheduled for (ISO)"><TextInput data-testid="t-sched" value={taskForm.scheduled_for || ""} onChange={(e) => setTaskForm({ ...taskForm, scheduled_for: e.target.value })} placeholder="2026-03-01T00:00:00Z" /></Field>
            <Field label="Notes"><TextArea data-testid="t-notes" rows={2} value={taskForm.notes || ""} onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setTaskModal(false)}>Cancel</Btn>
            <Btn onClick={createTask} data-testid="t-save">Create</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TRANSITION MODAL */}
      <Dialog open={!!transitionModal} onOpenChange={(v) => !v && setTransitionModal(null)}>
        <DialogContent>
          {transitionModal && (
            <>
              <DialogHeader>
                <DialogTitle>Transition task</DialogTitle>
                <DialogDescription>
                  Task <span className="mono">{transitionModal.id.slice(0, 8)}</span> · current: <Pill label={transitionModal.status} />
                </DialogDescription>
              </DialogHeader>
              <TransitionForm task={transitionModal} meta={meta} onSubmit={transitionTask} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TransitionForm({ task, meta, onSubmit }) {
  const [status, setStatus] = useState("executed");
  const [method, setMethod] = useState(task.method || "hard_delete");
  const [note, setNote] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [failureReason, setFailureReason] = useState("");

  const next = meta.task_statuses.filter(s => s !== task.status);
  return (
    <div className="grid gap-3">
      <Field label="Target status">
        <SelectInput data-testid="tt-status" value={status} onChange={(e) => setStatus(e.target.value)}>
          {next.map(s => <option key={s} value={s}>{s}</option>)}
        </SelectInput>
      </Field>
      <Field label="Method">
        <SelectInput data-testid="tt-method" value={method} onChange={(e) => setMethod(e.target.value)}>
          {meta.methods.map(m => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
        </SelectInput>
      </Field>
      <Field label="Evidence note">
        <TextArea data-testid="tt-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was deleted, where, and how." />
      </Field>
      <Field label="Processor confirmation">
        <TextInput data-testid="tt-conf" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} placeholder="e.g. AWS confirmation ID, ticket #" />
      </Field>
      {status === "failed" && (
        <Field label="Failure reason">
          <TextInput data-testid="tt-fail" value={failureReason} onChange={(e) => setFailureReason(e.target.value)} />
        </Field>
      )}
      <div className="flex justify-end">
        <Btn onClick={() => onSubmit({ status, method, evidence_note: note, processor_confirmation: confirmation, failure_reason: failureReason })} data-testid="tt-submit">
          Submit transition
        </Btn>
      </div>
    </div>
  );
}
